import React from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { COLORS } from 'Components/utils/color/colors';
import { ICONS } from 'Components/utils/icon/icons';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';
import { getListValue, t } from 'Src/i18n';
import { asWidgetProps } from 'Src/types/widget-helpers';
import type { ImageSlideShowProps } from 'Components/frameworks/image-slideshow/ImageSlideShow';
import 'Components/frameworks/image-slideshow/ImageSlideShow';
import type { LabeledIcon } from 'Components/frameworks/labeled-icons-list/LabeledIconsList';
import { LabeledIconsList } from 'Components/frameworks/labeled-icons-list/LabeledIconsList';
import { Sheet } from 'Components/frameworks/sheet/Sheet';
import { Zoom } from 'Components/frameworks/zoom/Zoom';
import { Text } from 'Components/frameworks/text/Text';
import { Title } from 'Components/frameworks/title/Title';
import { Tooltip } from 'Components/frameworks/tooltip/Tooltip';
import { Icon } from 'Components/frameworks/icon/Icon';
import { Image } from 'Components/frameworks/image/Image';
import type { MultimediaResources } from 'Components/widgets/multimedia/Multimedia';
import 'Components/widgets/multimedia/Multimedia';
import { Tabs } from 'Components/frameworks/tabs/Tabs';
import { ResortDistrictContainer } from 'Components/widgets/resort-district/ResortDistrictContainer';
import { goToHash } from 'Components/utils/scroll/scrollToHash';
type Props = {
  areas: Array<{
    id: string;
    label: string;
  }>;
  description?: string;
  immersive: any;
  isExclusiveCollection?: boolean;
  overviewCompact: Array<Array<string>>;
  overviewAlternate: Array<LabeledIcon>;
  strengthsLabel: string;
  amenitiesLabel: string;
  services: Array<{
    label: string;
    items: Array<LabeledIcon>;
  }>;
  floorPlans: Array<string>;
  slideshow?: Partial<ImageSlideShowProps>;
  multimedia?: MultimediaResources;
  productId?: string;
  title: string;
  subtitle: string;
};
const DIMENSIONS = {
  w: 'auto',
  h: 'auto',
};
export const ProductRoomDetail = ({
  areas = [],
  description,
  immersive,
  isExclusiveCollection = false,
  overviewCompact = [],
  overviewAlternate = [],
  strengthsLabel,
  amenitiesLabel,
  services = [],
  slideshow = {},
  multimedia = {},
  floorPlans = [],
  productId,
  title,
  subtitle,
}: Props) => {
  const slideshowProps = {
    ...slideshow,
    groupKey: 'roomDetail',
    hasInfiniteScroll: true,
    media: slideshow.media ?? [],
    nextLabel: t('common.next'),
    previousLabel: t('common.previous'),
    ratioDisplay: 0.8,
  };
  const { isTablet } = useResponsive();
  const numberOfPhotos = multimedia && multimedia.photos ? multimedia.photos.length : 0;
  const multimediaProps = {
    closeLabel: t('common.close'),
    descriptionLabels: {
      photos: '',
      streetViews: t('common.view360Disclaimer'),
    },
    galleryLabel: title
      ? t('common.galleries.productGallery', {
          productName: title,
        })
      : t('common.galleries.gallery'),
    isOverlay: true,
    labels: {
      photos: t('common.photoTitle'),
    },
    medias: multimedia,
    nextLabel: title
      ? t('common.galleries.productNextPicture', {
          productName: title,
        })
      : t('common.next'),
    previousLabel: title
      ? t('common.galleries.productPreviousPicture', {
          productName: title,
        })
      : t('common.previous'),
    pictureAltLabel: t('common.galleries.productPictureItem', {
      total: numberOfPhotos,
      productName: title,
    }),
  };

  const getHeader = (floorPlansLength, index) => {
    return floorPlansLength > 1
      ? {
          header: {
            title: `${index === 0 ? '' : '+'}${index}`,
          },
        }
      : null;
  };

  const getFloorplansTabs = () =>
    floorPlans.map((floorPlan, index) => {
      return {
        ...getHeader(floorPlans.length, index),
        content: (
          <div className="mx-auto w-full max-w-site-small" key={index}>
            <div className="flex flex-col">
              {!isTablet && (
                <Tooltip
                  trackingData={{
                    actionGA: 'layer',
                    labelGA: 'floorPlans',
                  }}
                  renderTrigger={(toggle) => (
                    <button
                      onClick={() => {
                        toggle();
                      }}
                      className="mb-15 ms-auto rounded-2 bg-white p-5 leading-0 text-black shadow-sm transition-colors duration-250 ease-in hocus:bg-black/60 hocus:text-white"
                      type="button"
                    >
                      <Icon name={ICONS.EXPAND} width="1.25rem" />
                    </button>
                  )}
                  modalProps={{
                    addendum: (
                      <Text
                        className="px-10 py-20 text-center text-12 leading-tight text-white"
                        text={t('common.roomPlan.disclaimer')}
                        theme="none"
                      />
                    ),
                    children: '',
                  }}
                >
                  <Zoom
                    alt={`${t('common.roomPlan.title')} ${title}`}
                    hint={t('common.roomPlan.swipeInstruction')}
                    imageUrl={floorPlan}
                  />
                </Tooltip>
              )}
              <div className="relative isolate h-0 w-full pb-[60%] md:pb-[50%]">
                <Image
                  altMsg={`${t('common.roomPlan.title')} ${title}`}
                  className="absolute flex h-full w-full items-center justify-center"
                  dimensions={DIMENSIONS}
                  imageUrl={floorPlan}
                  transformation="resize"
                />
              </div>
              <Text
                className="pb-40 pt-30 leading-tight md:pb-80 md:pt-25 md:text-center"
                text={t('common.roomPlan.disclaimer')}
              />
            </div>
          </div>
        ),
      };
    });

  const color = isExclusiveCollection ? COLORS.ULTRAMARINE : COLORS.BLACK;
  const hasResortDistrict =
    getListValue('common.resortDistrict.productIds', 15).includes(productId) && !!areas?.length;
  const header = (
    <section className="p-20 md:pt-80">
      <p className="mb-5 text-16 md:text-20">{subtitle}</p>
      <Title className={`text-${color}`} theme="title-brand-n44">
        {title}
      </Title>
      <ul className="mb-25 mt-20 flex gap-5 text-14 md:mb-50">
        {overviewCompact.map((item, index) => (
          <li
            className={classnames('md:basis-1/5', {
              'basis-1/2': !(hasResortDistrict && !!productId && areas.length > 0),
              'basis-1/3': hasResortDistrict && !!productId && areas.length > 0,
            })}
            key={index}
          >
            {item.map((line, idx) => (
              <span
                className={classnames('block', {
                  'font-bold': idx === 0,
                })}
                key={`${index}-${idx}`}
              >
                {line}
              </span>
            ))}
          </li>
        ))}
        {hasResortDistrict && !!productId && areas.length > 0 && (
          <li className="basis-1/3 md:basis-1/5">
            <span className="block font-bold">{t('common.resortDistrict.layerDistrictLabel')}</span>
            {areas.map((area) => (
              <div className="flex items-center gap-5" key={area.id}>
                <Icon name={ICONS.ADDRESS} />
                <button
                  className="text-initial underline"
                  onClick={() => goToHash('#room-location', true, 'smooth')}
                  type="button"
                >
                  {area.label}
                </button>
              </div>
            ))}
          </li>
        )}
      </ul>
      <Text
        className="mx-auto w-full max-w-site-small md:text-20"
        text={description}
        theme="text-sans-n3"
      />
    </section>
  );
  return (
    <Sheet
      fullWidthContent
      header={header}
      immersive={immersive}
      isExclusiveCollection={isExclusiveCollection}
      multimedia={multimediaProps}
      slideshow={slideshowProps}
    >
      {!!overviewAlternate.length && (
        <section className="mx-auto max-w-site-medium px-20 pb-20">
          <Title className={`mb-15 md:text-32 text-${color}`} hLevel={3} theme="title-brand-n3">
            {strengthsLabel}
          </Title>
          <div className="mx-auto w-full max-w-site-small">
            <LabeledIconsList color={color} items={overviewAlternate} />
          </div>
        </section>
      )}
      {floorPlans.length > 0 && (
        <section className="mx-auto max-w-site-medium px-20 pb-20">
          <Title className={`mb-40 md:text-32 text-${color}`} hLevel={3} theme="title-brand-n3">
            {t('common.roomPlan.title')}
          </Title>
          <Tabs
            id="product-accommodations-line-tabs"
            isContainerWide
            isContentUnmountedOnCollapse
            items={getFloorplansTabs()}
          />
        </section>
      )}
      {hasResortDistrict && !!productId && (
        <section className="relative pb-20">
          <div className="absolute top-0 -mt-60 h-0 w-0" id="room-location" />
          <Title
            className={`mx-auto mb-40 max-w-site-medium px-20 md:text-32 text-${color}`}
            hLevel={3}
            theme="title-brand-n3"
          >
            {t('common.resortDistrict.layerDistrictLabel')}
          </Title>
          <div className="pb-15 pt-60">
            <ResortDistrictContainer
              {...asWidgetProps<React.ComponentProps<typeof ResortDistrictContainer>>({
                access: t('common.resortDistrict.access'),
                resortId: productId,
                roomAreas: areas,
                roomLabel: title,
                trackingData: {
                  actionGA: `layer_${subtitle}`,
                },
              })}
            />
          </div>
        </section>
      )}
      <section className="mx-auto max-w-site-medium px-20">
        <Title className={`mb-15 md:text-32 text-${color}`} hLevel={3} theme="title-brand-n3">
          {amenitiesLabel}
        </Title>
        {services.map(({ label, items }) => (
          <section className="mx-auto w-full max-w-site-small py-15 md:py-30" key={label}>
            <Title
              className={`mb-30 border-b-1 border-ultramarine pb-10 md:text-20 text-${color}`}
              hLevel={4}
              theme="title-brand-n4"
            >
              {label}
            </Title>
            <LabeledIconsList color={color} items={items} theme="list" />
          </section>
        ))}
      </section>
    </Sheet>
  );
};
